import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

import { CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListNode, ListItemNode } from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ParagraphNode } from "lexical";

import { StyledParagraphNode } from "@/dashboard-design-system/RichTextInput/nodes/StyledParagraphNode";
import { VariableNode } from "@/dashboard-design-system/RichTextInput/nodes/VariableNode";

type ToolbarPluginName = "lists" | "fontSize";

type RichTextContext = {
  enableToolbarPlugin: (name: ToolbarPluginName) => void;
  disableToolbarPlugin: (name: ToolbarPluginName) => void;
  isToolbarPluginEnabled: (name: ToolbarPluginName) => boolean;
};

const RichTextContext = createContext<RichTextContext>({
  enableToolbarPlugin: () => {},
  disableToolbarPlugin: () => {},
  isToolbarPluginEnabled: () => false,
});

export const useRichTextContext = () => {
  return useContext(RichTextContext);
};

export const DashboardRichTextContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [enabledToolbarPlugins, setEnabledToolbarPlugins] = useState<ToolbarPluginName[]>([]);

  const composerConfig = useMemo(
    () => ({
      namespace: "lexical-editor",
      editable: true,
      nodes: [
        HeadingNode,
        QuoteNode,
        LinkNode,
        AutoLinkNode,
        ListNode,
        ListItemNode,
        CodeNode,
        HorizontalRuleNode,
        VariableNode,
        StyledParagraphNode,
        {
          replace: ParagraphNode,
          with: () => new StyledParagraphNode(),
          withKlass: StyledParagraphNode,
        },
      ],
      theme: {
        ltr: "lxcl-ltr",
        rtl: "lxcl-rtl",
        paragraph: "lxcl-paragraph",
        quote: "lxcl-quote",
        heading: {
          h1: "lxcl-h1",
          h2: "lxcl-h2",
          h3: "lxcl-h3",
          h4: "lxcl-h4",
          h5: "lxcl-h5",
          h6: "lxcl-h6",
        },
        list: {
          nested: {
            listitem: "lxcl-nested-listitem",
          },
          ol: "lxcl-ol",
          ul: "lxcl-ul",
          listitem: "lxcl-listItem",
          listitemChecked: "lxcl-listItemChecked",
          listitemUnchecked: "lxcl-listItemUnchecked",
        },
        hashtag: "lxcl-hashtag",
        image: "lxcl-image",
        link: "lxcl-link",
        text: {
          bold: "lxcl-bold",
          code: "lxcl-code",
          italic: "lxcl-italic",
          strikethrough: "lxcl-strikethrough",
          subscript: "lxcl-subscript",
          superscript: "lxcl-superscript",
          underline: "lxcl-underline",
          underlineStrikethrough: "lxcl-underlineStrikethrough",
        },
      },
      onError: (error: Error) => {
        // eslint-disable-next-line no-console
        console.error(error);
      },
    }),
    []
  );

  const enableToolbarPlugin = useCallback(
    (name: ToolbarPluginName) => setEnabledToolbarPlugins((p) => [...p, name]),
    []
  );
  const disableToolbarPlugin = useCallback(
    (name: ToolbarPluginName) => setEnabledToolbarPlugins((p) => p.filter((n) => n !== name)),
    []
  );

  const isToolbarPluginEnabled = useCallback(
    (name: ToolbarPluginName) => enabledToolbarPlugins.includes(name),
    [enabledToolbarPlugins]
  );

  const value = useMemo(
    () => ({
      enableToolbarPlugin,
      disableToolbarPlugin,
      isToolbarPluginEnabled,
    }),
    [enableToolbarPlugin, disableToolbarPlugin, isToolbarPluginEnabled]
  );

  return (
    <RichTextContext.Provider value={value}>
      <LexicalComposer initialConfig={composerConfig}>{children}</LexicalComposer>
    </RichTextContext.Provider>
  );
};
